import { useState } from 'react';
import { aiInsightAPI, TestRunFailure, TestRunResult } from 'api/editor/ai-insight';

/** Set after a failed preview's suggested prompt previews successfully (the adopt-flow
 *  "recovered" box): what was run and the original failure reason it fixes. */
export interface RecoveredSuggestion {
    suggestedPrompt: string;
    whyReason: string;
}

export default function useInsightPreviewRun(elementId: number, urlPrefix?: string) {
    const [loading, setLoading] = useState(false);
    const [previewingSuggested, setPreviewingSuggested] = useState(false);
    const [result, setResult] = useState<TestRunResult | null>(null);
    const [recovered, setRecovered] = useState<RecoveredSuggestion | null>(null);
    const [adopted, setAdopted] = useState(false);

    const run = async (
        prompt: string,
        isConditional: string,
        fallbackMessage: string,
        reportAiInsightId: number = 0,
        primaryResource: string = '',
    ) => {
        setLoading(true);
        setRecovered(null);
        setAdopted(false);
        setResult(null);
        try {
            const r = await aiInsightAPI.testRun(
                elementId,
                prompt,
                isConditional,
                fallbackMessage,
                urlPrefix,
                reportAiInsightId,
                primaryResource,
            );
            setResult(r);
        } finally {
            setLoading(false);
        }
    };

    /** Adopt flow step 1: preview the engine's suggested prompt as-is (nothing saved).
     *  Success keeps the result and remembers the suggestion so the user can adopt it;
     *  a repeat failure simply replaces the shown failure (possibly with a new offer). */
    const runSuggestedPrompt = async (
        suggestedPrompt: string,
        whyReason: string,
        isConditional: string,
        fallbackMessage: string,
        reportAiInsightId: number = 0,
        primaryResource: string = '',
    ) => {
        setPreviewingSuggested(true);
        setAdopted(false);
        try {
            const r = await aiInsightAPI.testRun(
                elementId,
                suggestedPrompt,
                isConditional,
                fallbackMessage,
                urlPrefix,
                reportAiInsightId,
                primaryResource,
            );
            setRecovered(r.status === 'success' ? { suggestedPrompt, whyReason } : null);
            setResult(r);
        } finally {
            setPreviewingSuggested(false);
        }
    };

    /** Adopt flow step 2: hand the suggested prompt to the caller (which writes it into the
     *  Prompt field) and switch the recovered box to the normal preview box. */
    const adoptSuggestedPrompt = (): string | null => {
        if (!recovered) {
            return null;
        }
        const prompt = recovered.suggestedPrompt;
        setRecovered(null);
        setAdopted(true);
        return prompt;
    };

    /** Hydrates the panel with the component's last stored failed generation on open, so the
     *  canvas's "see edit panel for details" promise holds. No-op once anything has run. */
    const seedFailure = (failure: TestRunFailure) => {
        if (result === null && !loading && !previewingSuggested) {
            setResult(failure);
        }
    };

    const reset = () => {
        setResult(null);
        setRecovered(null);
        setAdopted(false);
    };

    return {
        loading,
        previewingSuggested,
        result,
        recovered,
        adopted,
        run,
        runSuggestedPrompt,
        adoptSuggestedPrompt,
        seedFailure,
        reset,
    };
}
